/**
 * The board's shared chrome: the section lead-in that introduces a group of
 * panels, and the outlined card one panel renders in.
 */

import { Card, Typography } from "@norican/react";
import type { ReactNode } from "react";

interface PanelCardProps {
  /** Panel heading. */
  title: string;
  /** Muted line beside the heading — a count, a window, a coverage caveat. */
  caption?: ReactNode;
  /** Panel content. */
  children: ReactNode;
  /**
   * Body padding. `none` for panels whose content is a full-bleed `DataGrid`,
   * which brings its own.
   */
  padding?: "none" | "sm" | "md" | "lg";
}

export function PanelCard({ title, caption, children, padding = "md" }: PanelCardProps) {
  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            {title}
          </Typography>
          {caption && (
            <Typography variant="body-sm" tone="muted">
              {caption}
            </Typography>
          )}
        </div>
      </Card.Header>
      <Card.Body padding={padding}>{children}</Card.Body>
    </Card>
  );
}

interface SectionHeadingProps {
  /** Section title — one of the board's four answers. */
  title: string;
  /**
   * What this section does and does not claim.
   *
   * Carried here rather than left to the cards because a Defender section's
   * limits (no incident names, no per-person drill, counts that are floors) are
   * properties of the whole section, and stating them once above the panels is
   * what stops each card having to hedge on its own.
   */
  subtitle: string;
}

/** A section lead-in, so the panels below it read as one answer rather than four. */
export function SectionHeading({ title, subtitle }: SectionHeadingProps) {
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="heading-3" as="h2">
        {title}
      </Typography>
      <Typography variant="body-sm" tone="muted" as="p">
        {subtitle}
      </Typography>
    </div>
  );
}
